// src/components/auth/LoginForm.js
// UPDATED: Added dark theme support and mobile friendly styling
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import ForgotPasswordModal from './ForgotPasswordModal.js';
import { setRegistrationHandoff, clearRegistrationHandoff } from '../../utils/registrationHandoff.js';

const LoginForm = ({ onClose }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  // Set when the server reports a verified account that never joined or
  // created a company. Rendered as guidance rather than as a login error,
  // because nothing is wrong with their credentials.
  const [registrationIncomplete, setRegistrationIncomplete] = useState(false);

  // The member identity that 403 came back with: { memberId, email, firstName,
  // lastName }. No token, no permissions — see authController's note on why a
  // company-less account still gets no session. It exists so the two buttons
  // below can hand the person straight to the company step instead of making
  // them re-prove who they are.
  const [resumeMember, setResumeMember] = useState(null);

  // Close the modal first, then navigate — otherwise the modal can outlive
  // the route change and sit on top of the new page.
  //
  // TWO DESTINATIONS, ONE FUNCTION.
  //
  //   NO resumeMember  a fresh visitor who picked "create" or "join" from the
  //                    New to Quickbid panel. `intent` alone, which Registration
  //                    applies when they reach the create-or-join step. Step 1.
  //
  //   resumeMember     someone who just proved their password against an
  //                    account that never got a company. `resume=true` makes
  //                    Registration skip name/email/password/verification —
  //                    all of which they already completed — and `intent` then
  //                    takes them the rest of the way to step 4 (create) or 5
  //                    (join). The identity rides in ROUTER STATE rather than
  //                    the URL: a memberId in a query string ends up in
  //                    history, logs and shared links.
  //
  // The password is DELIBERATELY not passed. React Router state is written to
  // history.state, which browsers persist to disk for session restore — so a
  // password put here would be written to the user's drive. That is why
  // finishing this way ends with one sign-in rather than an automatic one.
  const goToRegister = (intent) => {
    if (onClose) onClose();
    const resuming = !!(resumeMember && resumeMember.memberId);

    // THE PASSWORD GOES IN MEMORY, THE IDENTITY GOES IN THE URL'S STATE.
    //
    // Finishing this registration means creating a company and then going to
    // Stripe checkout — and checkout needs a session, which this person does not
    // have (the server refused their sign-in for having no company). The only
    // way to get one is to call login() once the company exists, and that needs
    // the password. It is sitting in `password` state right here, entered
    // seconds ago; without handing it over, Registration can create the company
    // and then has no way to take them to payment, which strands them on the
    // free tier.
    //
    // setRegistrationHandoff keeps it in a module variable — the JS heap of this
    // page load. navigate() below is a client-side route change, so it survives
    // exactly that hop and nothing further. A refresh or a new tab loses it, and
    // Registration falls back to asking them to sign in once.
    //
    // NOT in the router state beside resumeMember: that is serialized into
    // history.state and written to disk for session restore. Identity there is
    // fine; a password is not.
    if (resuming) {
      setRegistrationHandoff({
        memberId: resumeMember.memberId,
        email: resumeMember.email || email,
        firstName: resumeMember.firstName,
        lastName: resumeMember.lastName,
        password,
      });
    }

    setTimeout(() => {
      navigate(
        resuming ? `/register?resume=true&intent=${intent}` : `/register?intent=${intent}`,
        resuming ? { state: { resumeMember } } : undefined
      );
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setRegistrationIncomplete(false);
    setResumeMember(null);
    // Any stash from a previous attempt is stale the moment they submit again —
    // possibly a different address, possibly a corrected password. setRegistration-
    // Handoff would supersede it later anyway, but only if this attempt also ends
    // in the incomplete-registration branch. Clearing here covers the attempt that
    // succeeds, or fails some other way, and would otherwise leave the old
    // credential in memory until its timer expired.
    clearRegistrationHandoff();

    try {
      setIsLoading(true);
      setError('');
      
      // Attempt login.
      //
      // An account belonging to no company is rejected by the server with a 403
      // rather than being let in, so there is no "not an employee" case to
      // handle on the success path.
      //
      // THE RESUME PATH IS BACK, AND THE OBJECTION TO IT NO LONGER APPLIES.
      // It was removed because people landed in a half-finished registration
      // "without understanding how they got there, or what to do next" — a fair
      // complaint about a silent redirect. The amber panel below now states the
      // situation and offers the two ways out, so nobody arrives at that step
      // by surprise; they get there by pressing a button that says what it
      // does. What was actually wrong was the lack of explanation, not the
      // resuming.
      //
      // The alternative — start again — costs them their name, email, password
      // and a fresh verification code to re-reach a step they had already
      // passed. That is a worse answer to "you are nearly done".
      await login(email, password);

      if (onClose) {
        onClose();
      }

      setTimeout(() => {
        navigate('/orders');
      }, 100);
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific error cases
      //
      // 403 registrationIncomplete: the account exists and the password is
      // right, but it belongs to no company, so nothing in the app would work.
      // Say so plainly and point at the way out. The flag is checked first and
      // the message second, because services in this codebase rethrow plain
      // Errors that lose err.response.
      const incomplete =
        err.registrationIncomplete ||
        err.data?.registrationIncomplete ||
        err.response?.data?.registrationIncomplete ||
        (err.message || '').includes('registration was never completed');

      if (incomplete) {
        setRegistrationIncomplete(true);
        // Identity for the resume handoff. May be absent against an older
        // server that predates it — the panel and both buttons still work, they
        // just fall back to starting registration from step 1, which is exactly
        // today's behaviour. Nothing here depends on it being present.
        setResumeMember(err.resumeMember || err.data?.resumeMember || null);
        setError('');
      } else if (
        // Read the flag, not the sentence. authService now preserves the
        // response body, so this no longer hangs on an exact string match
        // against a message defined in another file. The prose comparison stays
        // as a fallback for an older server.
        err.data?.emailNotVerified ||
        err.message === 'Please verify your email before logging in'
      ) {
        setError('Please verify your email before logging in. Check your inbox for the verification code.');
      } else if (err.message === 'Invalid credentials') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(err.message || 'Failed to log in. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPasswordSuccess = () => {
    setShowForgotPassword(false);
    setPasswordResetSuccess(true);
    setError('');
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setPasswordResetSuccess(false);
    }, 5000);
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md border border-red-200 dark:border-red-800 text-sm">
            {error}
          </div>
        )}

        {registrationIncomplete && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              That registration was never finished
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              Your account was created but never linked to a company, so there is
              nothing to sign in to yet. Start again below and it will only take
              a minute.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => goToRegister('create')}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              >
                Create a company
              </button>
              <button
                type="button"
                onClick={() => goToRegister('join')}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-white dark:bg-slate-700 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors"
              >
                Join my company
              </button>
            </div>
          </div>
        )}

        {passwordResetSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md border border-green-200 dark:border-green-800 text-sm">
            Password reset successfully! Please log in with your new password.
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2.5 px-3 
                       bg-white dark:bg-slate-700 text-gray-900 dark:text-white 
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                       dark:focus:ring-blue-400 dark:focus:border-blue-400
                       text-base sm:text-sm transition-colors"
            placeholder="your@email.com"
            required
            autoComplete="email"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2.5 px-3 pr-10 
                         bg-white dark:bg-slate-700 text-gray-900 dark:text-white 
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         dark:focus:ring-blue-400 dark:focus:border-blue-400
                         text-base sm:text-sm transition-colors"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="text-sm text-right">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
          >
            Forgot password?
          </button>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                       bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                       dark:focus:ring-offset-slate-800
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      </form>

      {/* REGISTER — two doors, not one.
          Asking "create or join?" here means registration never has to ask it
          again, and the two paths genuinely differ: one chooses a plan and
          pays, the other needs an access code from an admin and pays nothing.
          A single "Register" button would defer that decision to a step where
          it has less context. */}
      <div className="mt-6 pt-5 border-t border-gray-200 dark:border-slate-700">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          New to Quickbid?
        </p>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => goToRegister('create')}
            className="w-full text-left px-4 py-3 rounded-md border border-gray-300 dark:border-slate-600 
                       bg-white dark:bg-slate-700 hover:border-blue-400 dark:hover:border-blue-500 
                       transition-colors"
          >
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              Create a company
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Choose a plan and set up your shop. You become the administrator.
            </span>
          </button>

          <button
            type="button"
            onClick={() => goToRegister('join')}
            className="w-full text-left px-4 py-3 rounded-md border border-gray-300 dark:border-slate-600 
                       bg-white dark:bg-slate-700 hover:border-blue-400 dark:hover:border-blue-500 
                       transition-colors"
          >
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              Join my company
            </span>
            {/* Stated up front because it is the thing people cannot supply
                for themselves. Someone who reads this and realises they need
                to ask their admin has been saved a dead end three steps in. */}
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              By invitation. You&apos;ll need the company name and an access code
              from your administrator.
            </span>
          </button>
        </div>
      </div>

      {onClose && (
        <div className="mt-4">
          <button
            onClick={onClose}
            className="w-full text-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSuccess={handleForgotPasswordSuccess}
        />
      )}
    </div>
  );
};

export default LoginForm;